import {
    useEffect,
    useState,
    Fragment,
    useRef,
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type IUserShortcutProps from "./type";
import type {
    UserShortcutActionPressConstantType,
    UserShortcutPopupPropsType,
    UserShortcutVariant
} from "./type";
import stylesheet, {
    getUserShortcutStatusDotStyle,
    getUserShortcutStatusColorKey,
    getUserShortcutContainerColor,
    getUserShortcutActionRadius,
    USER_SHORTCUT_AVATAR_SIZES,
    getUserShortcutMeasures,
    getUserShortcutColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    useContext as useCoreComplexContext
} from "../../core/contexts/coreComplex";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    ChevronDown as ChevronDownIcon,
    ChevronUp as ChevronUpIcon,
    LogOut as LogOutIcon,
    LogIn as LogInIcon,
    type LucideIcon
} from "lucide-react-native";
import {
    AVATAR_STATUS_LABELS,
    getAvatarSizeKey
} from "../avatar/stylesheet";
import {
    getMirrorStyle,
    isMirroredIcon
} from "../../helpers/direction";
import DropdownMenu from "../dropdownMenu";
import BottomSheet from "../bottomSheet";
import StateLayer from "../stateLayer";
import Separator from "../separator";
import Avatar from "../avatar";
import Button from "../button";
import Text from "../text";

const UserShortcutChevronIcon: Record<Exclude<UserShortcutVariant, "compact">, LucideIcon> = {
    default: ChevronDownIcon,
    drawer: ChevronUpIcon
};

const UserShortcutLoginIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => {
    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    return <View
        style={getMirrorStyle({
            isMirrored: isMirroredIcon({
                name: "LogIn"
            }),
            direction
        })}
    >
        <LogInIcon
            color={customColor}
            size={size}
        />
    </View>;
};

const UserShortcutLogoutIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => {
    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    return <View
        style={getMirrorStyle({
            isMirrored: isMirroredIcon({
                name: "LogOut"
            }),
            direction
        })}
    >
        <LogOutIcon
            color={customColor}
            size={size}
        />
    </View>;
};

const UserShortcut: FC<IUserShortcutProps> = ({
    isCloseBottomSheetOnLogoutPress = true,
    isShowLoginButtonWhenNoSession = true,
    onLogoutPress: onLogoutPressProp,
    isShowRegisterButton = false,
    isWorkWithCoreComplex = true,
    isShowLogoutButton = true,
    isShowArrowButton = true,
    isSessionActive = true,
    variant = "default",
    isDisabled = false,
    backgroundColor,
    onRegisterPress,
    spreadBehaviour,
    customLocalize,
    actionButtons,
    onLoginPress,
    avatarProps,
    customTheme,
    statusColor,
    statusText,
    maxWidth,
    subTitle,
    onPress,
    badges,
    email,
    title,
    style
}) => {
    const {
        shapeConfigs,
        touchTargets,
        avatarSizes,
        shadowAlpha,
        elevations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const containerRef = useRef<View>(null);

    const isFocusRestorePendingRef = useRef(false);

    const {
        client: coreComplexClient
    } = useCoreComplexContext();

    const [
        isBottomSheetOpen,
        setIsBottomSheetOpen
    ] = useState(false);

    const [
        isMenuOpen,
        setIsMenuOpen
    ] = useState(false);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled
    });

    const isSignedIn = isWorkWithCoreComplex ? !!coreComplexClient?.variables?.accessToken : isSessionActive;

    const isBottomSheetEnabled = isSignedIn && isShowArrowButton;

    const isMenuEnabled = isBottomSheetEnabled && pointerType === "fine";

    const isSheetFormEnabled = isBottomSheetEnabled && !isMenuEnabled;

    const isSignedOutActionVisible = isShowLoginButtonWhenNoSession || isShowRegisterButton;

    const isCompactLogin = !isSignedIn && variant === "compact" && isShowLoginButtonWhenNoSession;

    const isInteractive = isSignedIn ? isBottomSheetEnabled || !!onPress : isCompactLogin || (!isSignedOutActionVisible && !!onPress);

    const statusIndicatorType = avatarProps?.statusIndicatorType;

    const currentStatusText = statusText || (statusIndicatorType ? localize(AVATAR_STATUS_LABELS[statusIndicatorType]) : undefined);

    const currentStatusColorKey = getUserShortcutStatusColorKey({
        statusIndicatorType,
        statusColor
    });

    const currentBackgroundColor = backgroundColor ?? (variant === "default" ? "raised" : "transparent");

    const surfaceBackgroundColor = currentBackgroundColor === "transparent" ? undefined : currentBackgroundColor;

    const isListSurface = variant === "default" && !!surfaceBackgroundColor;

    const currentAvatarSize = avatarProps?.size ?? USER_SHORTCUT_AVATAR_SIZES.trigger;

    const currentMeasures = getUserShortcutMeasures({
        avatarSize: avatarSizes[getAvatarSizeKey({
            size: currentAvatarSize
        })],
        isListSurface,
        touchTargets,
        pointerType,
        radiuses,
        borders,
        variant,
        spaces
    });

    const currentColors = getUserShortcutColors({
        statusColor: currentStatusColorKey,
        isDisabled,
        colors
    });

    const sheetSubTitle = email || subTitle;

    const isSheetHeaderVisible = !!title || !!sheetSubTitle || !!currentStatusText;

    const {
        subTitleContainer: subTitleContainerDynamicStyle,
        triggerContainer: triggerContainerDynamicStyle,
        sheetContent: sheetContentDynamicStyle,
        sheetHeader: sheetHeaderDynamicStyle,
        menuHeader: menuHeaderDynamicStyle,
        container: containerDynamicStyle,
        statusRow: statusRowDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        spreadBehaviour: spreadBehaviour ?? (variant === "drawer" ? "stretch" : "baseline"),
        isFocusVisible: isInteractive && isFocusVisible,
        containerColor: getUserShortcutContainerColor({
            backgroundColor: currentBackgroundColor,
            colors
        }),
        currentMeasures,
        isListSurface,
        shapeConfigs,
        shadowAlpha,
        elevations,
        maxWidth,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    useEffect(() => {
        if(Platform.OS !== "web" || !isFocusRestorePendingRef.current) {
            return;
        }

        isFocusRestorePendingRef.current = false;

        const activeElement = document.activeElement;

        if(activeElement && activeElement !== document.body) {
            return;
        }

        (containerRef.current as unknown as HTMLElement | null)?.querySelector<HTMLElement>("button, [role=\"button\"], [tabindex]")?.focus();
    }, [
        isSignedIn
    ]);

    const popupProps: UserShortcutPopupPropsType = Platform.OS === "web" && isBottomSheetEnabled ? {
        "aria-haspopup": isMenuEnabled ? "menu" : "dialog"
    } : {};

    const getTriggerLabel = () => {
        if(!isSignedIn) {
            return localize("login");
        }

        if(variant !== "compact" && (title || subTitle || currentStatusText)) {
            return undefined;
        }

        const name = title || avatarProps?.title;

        const nameLabel = name && currentStatusText ? localize("avatar-with-status", [
            name,
            currentStatusText
        ]) : name || currentStatusText;

        if(!isBottomSheetEnabled) {
            return nameLabel;
        }

        if(!nameLabel) {
            return localize("open-account-menu");
        }

        return localize("user-shortcut-label", [
            nameLabel
        ]);
    };

    const onTriggerPress = () => {
        if(isCompactLogin) {
            (onLoginPress ?? onPress)?.();

            return;
        }

        if(onPress) {
            onPress();
        }

        if(!isSheetFormEnabled) {
            return;
        }

        setIsBottomSheetOpen(!isBottomSheetOpen);
    };

    const onMenuOpenChange = (isOpen: boolean) => {
        setIsMenuOpen(isOpen);
    };

    const onBottomSheetOpenChange = (isOpen: boolean) => {
        setIsBottomSheetOpen(isOpen);
    };

    const onActionPress = ({
        isCloseBottomSheetOnPress,
        onPress: onActionPressProp
    }: UserShortcutActionPressConstantType) => {
        if(isCloseBottomSheetOnPress !== false) {
            setIsBottomSheetOpen(false);
        }

        if(onActionPressProp) {
            onActionPressProp();
        }
    };

    const onLogoutPress = async () => {
        isFocusRestorePendingRef.current = true;

        if(isCloseBottomSheetOnLogoutPress) {
            setIsBottomSheetOpen(false);
        }

        let response;
        let error;

        if(isWorkWithCoreComplex && coreComplexClient) {
            try {
                response = await (
                    coreComplexClient.NAuth as unknown as {
                        logout: () => Promise<unknown>;
                    }
                ).logout();
            } catch(e) {
                error = e;
            }
        }

        if(onLogoutPressProp) {
            onLogoutPressProp(response, error);
        }
    };

    const renderBadges = () => {
        if(!isSignedIn || !badges || badges.length === 0) {
            return null;
        }

        return badges.map((badge, index) => {
            return <View
                style={[
                    stylesheet.badgeContainer,
                    {
                        zIndex: shapeConfigs.layers.raised + index
                    }
                ]}
                key={index}
            >
                {badge}
            </View>;
        });
    };

    const renderAvatar = () => {
        if(!isSignedIn) {
            return <Avatar
                {...avatarProps}
                customLocalize={avatarProps?.customLocalize ?? customLocalize}
                customTheme={avatarProps?.customTheme ?? customTheme}
                backgroundColor={undefined}
                isStatusIndicator={false}
                size={currentAvatarSize}
                isWorkWithAction={false}
                isDisabled={isDisabled}
                imageSource={undefined}
                imageUrl={undefined}
                aria-hidden={true}
                image={undefined}
                title={undefined}
                type="neutral"
            />;
        }

        return <Avatar
            {...avatarProps}
            isStatusIndicator={!isDisabled && (avatarProps?.isStatusIndicator ?? !!currentStatusText)}
            avatarBackgroundColor={avatarProps?.avatarBackgroundColor ?? surfaceBackgroundColor}
            customLocalize={avatarProps?.customLocalize ?? customLocalize}
            customTheme={avatarProps?.customTheme ?? customTheme}
            isDisabled={isDisabled || !!avatarProps?.isDisabled}
            size={currentAvatarSize}
            isWorkWithAction={false}
            aria-hidden={true}
        />;
    };

    const renderAvatarContainer = () => {
        return <View
            style={stylesheet.avatarContainer}
        >
            {renderAvatar()}
            {renderBadges()}
        </View>;
    };

    const renderStatus = (isShrinkable: boolean) => <View
        style={[
            stylesheet.statusRow,
            statusRowDynamicStyle,
            isShrinkable ? stylesheet.status : null
        ]}
    >
        <View
            style={getUserShortcutStatusDotStyle({
                color: currentColors.statusDotColor,
                shapeConfigs,
                radiuses,
                borders,
                spaces
            })}
            aria-hidden={true}
        />
        <Text
            customColor={currentColors.statusColor}
            customTheme={customTheme}
            style={stylesheet.status}
            variant="labelSmallSize"
            ellipsizeMode="tail"
            numberOfLines={1}
        >
            {currentStatusText}
        </Text>
    </View>;

    const renderSubTitle = () => {
        if(!subTitle && !currentStatusText) {
            return null;
        }

        return <View
            style={[
                stylesheet.subTitleContainer,
                subTitleContainerDynamicStyle
            ]}
        >
            {
                currentStatusText ?
                    renderStatus(true)
                :
                    null
            }
            {
                currentStatusText && subTitle ?
                    <Text
                        customColor={currentColors.subTitleColor}
                        customTheme={customTheme}
                        variant="labelSmallSize"
                        aria-hidden={true}
                    >
                        •
                    </Text>
                :
                    null
            }
            {
                subTitle ?
                    <Text
                        customColor={currentColors.subTitleColor}
                        style={stylesheet.subTitle}
                        customTheme={customTheme}
                        variant="labelSmallSize"
                        ellipsizeMode="tail"
                        numberOfLines={1}
                    >
                        {subTitle}
                    </Text>
                :
                    null
            }
        </View>;
    };

    const renderContent = () => {
        if(!isSignedIn || variant === "compact") {
            return null;
        }

        return <View
            style={stylesheet.contentContainer}
        >
            {
                title ?
                    <Text
                        customColor={currentColors.titleColor}
                        customTheme={customTheme}
                        style={stylesheet.title}
                        variant="labelLargeSize"
                        ellipsizeMode="tail"
                        numberOfLines={1}
                    >
                        {title}
                    </Text>
                :
                    null
            }
            {renderSubTitle()}
        </View>;
    };

    const renderChevron = () => {
        if(!isBottomSheetEnabled || variant === "compact") {
            return null;
        }

        const ChevronIcon = UserShortcutChevronIcon[variant];

        return <View
            style={stylesheet.chevronContainer}
            aria-hidden={true}
        >
            <ChevronIcon
                color={currentColors.iconColor}
                size={iconSizes.medium}
            />
        </View>;
    };

    const renderSignedOutActions = () => {
        if(isSignedIn || variant === "compact" || !isSignedOutActionVisible) {
            return null;
        }

        const actionRadius = getUserShortcutActionRadius({
            currentMeasures,
            isListSurface,
            radiuses,
            borders,
            spaces
        });

        return <Fragment>
            <View
                style={stylesheet.spacer}
            />
            {
                isShowRegisterButton ?
                    <Button
                        onPress={() => onRegisterPress?.()}
                        title={localize("register")}
                        borderRadius={actionRadius}
                        customTheme={customTheme}
                        isDisabled={isDisabled}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="primary"
                        size="small"
                    />
                :
                    null
            }
            {
                isShowLoginButtonWhenNoSession ?
                    <Button
                        onPress={() => {
                            isFocusRestorePendingRef.current = true;

                            onLoginPress?.();
                        }}
                        icon={UserShortcutLoginIcon}
                        borderRadius={actionRadius}
                        title={localize("login")}
                        customTheme={customTheme}
                        isDisabled={isDisabled}
                        spreadBehaviour="free"
                        variant="filled"
                        type="primary"
                        size="small"
                    />
                :
                    null
            }
        </Fragment>;
    };

    const renderPressable = () => {
        const isTriggerExpanded = isMenuEnabled ? isMenuOpen : isBottomSheetOpen;

        return <Pressable
            {...interactionHandlers}
            {...popupProps}
            aria-expanded={isBottomSheetEnabled ? isTriggerExpanded : undefined}
            style={[
                stylesheet.triggerContainer,
                triggerContainerDynamicStyle
            ]}
            aria-label={getTriggerLabel()}
            aria-disabled={isDisabled}
            onPress={onTriggerPress}
            disabled={isDisabled}
            role="button"
        >
            <View
                style={[
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                <StateLayer
                    isPressed={isPressed || (isMenuEnabled && isMenuOpen)}
                    borderRadius={currentMeasures.borderRadius}
                    customTheme={customTheme}
                    isHovered={isHovered}
                />
                {renderAvatarContainer()}
                {renderContent()}
                {renderChevron()}
            </View>
        </Pressable>;
    };

    const renderTrigger = () => {
        if(!isInteractive) {
            return <View
                style={[
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                {renderAvatarContainer()}
                {renderContent()}
                {renderSignedOutActions()}
            </View>;
        }

        if(!isMenuEnabled) {
            return renderPressable();
        }

        return <DropdownMenu
            title={localize("account-menu")}
            onOpenChange={onMenuOpenChange}
            customLocalize={customLocalize}
            customTheme={customTheme}
            isOpen={isMenuOpen}
            side="right"
            align="end"
        >
            <DropdownMenu.Trigger
                style={stylesheet.menuAnchor}
                customTheme={customTheme}
                isDisabled={isDisabled}
            >
                {renderPressable()}
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
                customTheme={customTheme}
            >
                {renderMenuHeader()}
                {renderMenuDivider()}
                {renderMenuActions()}
                {renderMenuLogout()}
            </DropdownMenu.Content>
        </DropdownMenu>;
    };

    const renderSheetHeader = () => {
        if(!isSheetHeaderVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.sheetHeader,
                sheetHeaderDynamicStyle
            ]}
        >
            <Avatar
                {...avatarProps}
                isStatusIndicator={avatarProps?.isStatusIndicator ?? !!currentStatusText}
                avatarBackgroundColor={avatarProps?.avatarBackgroundColor ?? "raised"}
                customLocalize={avatarProps?.customLocalize ?? customLocalize}
                customTheme={avatarProps?.customTheme ?? customTheme}
                size={USER_SHORTCUT_AVATAR_SIZES.sheet}
                isWorkWithAction={false}
                aria-hidden={true}
            />
            <View
                style={stylesheet.sheetHeaderContent}
            >
                {
                    title ?
                        <Text
                            customTheme={customTheme}
                            variant="titleLargeSize"
                            color="high"
                        >
                            {title}
                        </Text>
                    :
                        null
                }
                {
                    sheetSubTitle ?
                        <Text
                            customTheme={customTheme}
                            variant="bodyMediumSize"
                            ellipsizeMode="tail"
                            numberOfLines={1}
                            color="mid"
                        >
                            {sheetSubTitle}
                        </Text>
                    :
                        null
                }
                {
                    currentStatusText ?
                        renderStatus(false)
                    :
                        null
                }
            </View>
        </View>;
    };

    const renderSheetDivider = () => {
        if(!isSheetHeaderVisible || (!actionButtons?.length && !isShowLogoutButton)) {
            return null;
        }

        return <Separator
            customLocalize={customLocalize}
            customTheme={customTheme}
        />;
    };

    const renderActionButtons = () => {
        if(!actionButtons) {
            return null;
        }

        return actionButtons.map((actionButton, index) => {
            const {
                isCloseBottomSheetOnPress,
                ...buttonProps
            } = actionButton;

            return <Button
                customTheme={customTheme}
                variant="tonal"
                type="neutral"
                size="large"
                {...buttonProps}
                onPress={() => onActionPress({
                    onPress: buttonProps.onPress,
                    isCloseBottomSheetOnPress
                })}
                spreadBehaviour="stretch"
                key={index}
            />;
        });
    };

    const renderLogoutButton = () => {
        if(!isShowLogoutButton) {
            return null;
        }

        return <Button
            icon={UserShortcutLogoutIcon}
            title={localize("logout")}
            customTheme={customTheme}
            spreadBehaviour="stretch"
            onPress={onLogoutPress}
            variant="outline"
            type="danger"
            size="large"
        />;
    };

    const renderMenuHeader = () => {
        if(!isSheetHeaderVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.sheetHeader,
                menuHeaderDynamicStyle
            ]}
        >
            <Avatar
                {...avatarProps}
                isStatusIndicator={avatarProps?.isStatusIndicator ?? !!currentStatusText}
                avatarBackgroundColor={avatarProps?.avatarBackgroundColor ?? "raised"}
                customLocalize={avatarProps?.customLocalize ?? customLocalize}
                customTheme={avatarProps?.customTheme ?? customTheme}
                size={USER_SHORTCUT_AVATAR_SIZES.menu}
                isWorkWithAction={false}
                aria-hidden={true}
            />
            <View
                style={stylesheet.sheetHeaderContent}
            >
                {
                    title ?
                        <Text
                            customTheme={customTheme}
                            variant="labelMediumSize"
                            ellipsizeMode="tail"
                            numberOfLines={1}
                            color="high"
                        >
                            {title}
                        </Text>
                    :
                        null
                }
                {
                    sheetSubTitle ?
                        <Text
                            customTheme={customTheme}
                            variant="bodySmallSize"
                            ellipsizeMode="tail"
                            numberOfLines={1}
                            color="low"
                        >
                            {sheetSubTitle}
                        </Text>
                    :
                        null
                }
                {
                    currentStatusText ?
                        renderStatus(false)
                    :
                        null
                }
            </View>
        </View>;
    };

    const renderMenuDivider = () => {
        if(!isSheetHeaderVisible || (!actionButtons?.length && !isShowLogoutButton)) {
            return null;
        }

        return <DropdownMenu.Separator
            customTheme={customTheme}
        />;
    };

    const renderMenuActions = () => {
        if(!actionButtons) {
            return null;
        }

        return actionButtons.map((actionButton, index) => {
            const {
                isCloseBottomSheetOnPress,
                ...buttonProps
            } = actionButton;

            return <DropdownMenu.Item
                title={buttonProps.title ?? buttonProps["aria-label"] ?? ""}
                type={buttonProps.type === "danger" ? "danger" : "neutral"}
                isCloseOnSelect={isCloseBottomSheetOnPress !== false}
                isDisabled={buttonProps.isDisabled}
                onPress={buttonProps.onPress}
                customTheme={customTheme}
                icon={buttonProps.icon}
                key={index}
            />;
        });
    };

    const renderMenuLogout = () => {
        if(!isShowLogoutButton) {
            return null;
        }

        return <Fragment>
            {
                actionButtons?.length ?
                    <DropdownMenu.Separator
                        customTheme={customTheme}
                    />
                :
                    null
            }
            <DropdownMenu.Item
                isCloseOnSelect={isCloseBottomSheetOnLogoutPress}
                icon={UserShortcutLogoutIcon}
                title={localize("logout")}
                customTheme={customTheme}
                onPress={onLogoutPress}
                type="danger"
            />
        </Fragment>;
    };

    const renderBottomSheet = () => {
        if(!isSheetFormEnabled) {
            return null;
        }

        return <BottomSheet
            modalProps={{
                "aria-label": localize("account-menu")
            }}
            onOpenChange={onBottomSheetOpenChange}
            customLocalize={customLocalize}
            isOpen={isBottomSheetOpen}
            isWorkWithAnimation={true}
            customTheme={customTheme}
        >
            <View
                style={[
                    stylesheet.sheetContent,
                    sheetContentDynamicStyle
                ]}
            >
                {renderSheetHeader()}
                {renderSheetDivider()}
                {renderActionButtons()}
                {renderLogoutButton()}
            </View>
        </BottomSheet>;
    };

    return <Fragment>
        <View
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            ref={containerRef}
        >
            {renderTrigger()}
        </View>
        {renderBottomSheet()}
    </Fragment>;
};
export default UserShortcut;
